import React, { useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useDropzone } from 'react-dropzone';
import {
  IconSparkles,
  IconArrowRight,
  IconPaperclip,
  IconX,
} from '@tabler/icons-react';
import { Box, Button, Flex, Text } from '~/design-system';
import type { ImporterDescriptor } from '../../descriptors';
import { useFileAttachment } from './FileAttachmentContext';

type PromptInputProps = {
  query: string;
  onQueryChange: (next: string) => void;
  topMatch: ImporterDescriptor | undefined;
};

export default function PromptInput({
  query,
  onQueryChange,
  topMatch,
}: PromptInputProps) {
  const { i18n } = useLingui();
  const history = useHistory();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const inputRef = useRef<HTMLInputElement>(null);
  const { pendingFile, attachFile, clearPendingFile } = useFileAttachment();

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept:
      '.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv',
    onDrop: (accepted) => {
      const [file] = accepted;
      if (file) attachFile(file);
    },
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && inputRef.current === document.activeElement) {
        onQueryChange('');
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onQueryChange]);

  function onContinue() {
    if (!topMatch) return;
    history.push(`/${workspaceId}/${topMatch.id}`);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && topMatch) {
      e.preventDefault();
      onContinue();
    }
  }

  return (
    <Box
      {...getRootProps()}
      sx={{
        backgroundColor: 'white',
        borderRadius: 12,
        border: '1px solid',
        borderColor: isDragActive ? '#2563eb' : '#e5e7eb',
        boxShadow: isDragActive
          ? '0 0 0 3px rgba(37,99,235,0.15)'
          : '0 1px 2px rgba(0,0,0,0.04)',
        padding: 3,
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
      }}
    >
      <input {...getInputProps()} />

      {pendingFile && (
        <Flex
          sx={{
            alignItems: 'center',
            gap: 2,
            marginBottom: 2,
            padding: '6px 10px',
            borderRadius: 8,
            backgroundColor: '#eef2ff',
            border: '1px solid #c7d2fe',
            width: 'fit-content',
            maxWidth: '100%',
          }}
        >
          <IconPaperclip size={14} stroke={1.75} color="#4338ca" />
          <Text
            sx={{
              fontSize: '13px',
              color: '#1f2937',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {pendingFile.name}
          </Text>
          {/* @ts-ignore — Box typing doesn't surface button-element props */}
          <Box
            as="button"
            type="button"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              clearPendingFile();
            }}
            sx={{
              background: 'transparent',
              border: 'none',
              color: '#6b7280',
              cursor: 'pointer',
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              '&:hover': { color: '#111827' },
            }}
            aria-label={i18n._(t`Remove attached file`)}
          >
            <IconX size={14} stroke={2} />
          </Box>
        </Flex>
      )}

      <Flex sx={{ alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 6,
            backgroundColor: '#dbeafe',
            color: '#2563eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <IconSparkles size={18} stroke={1.75} />
        </Box>
        {/* @ts-ignore — Box typing doesn't surface input-element props */}
        <Box
          as="input"
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onQueryChange(e.target.value)
          }
          onKeyDown={onKeyDown}
          aria-label={i18n._(t`Describe what you want to import`)}
          placeholder={i18n._(
            t`Describe what you want to import, or drop a file here…`
          )}
          sx={{
            flex: 1,
            border: 'none',
            outline: 'none',
            fontSize: '16px',
            padding: 2,
            backgroundColor: 'transparent',
          }}
        />
        {/* @ts-ignore — Box typing doesn't surface button-element props */}
        <Box
          as="button"
          type="button"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            open();
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 6,
            border: 'none',
            background: 'transparent',
            color: '#6b7280',
            cursor: 'pointer',
            flexShrink: 0,
            '&:hover': {
              color: '#2563eb',
              backgroundColor: '#f5f9ff',
            },
          }}
          aria-label={i18n._(t`Attach a file`)}
        >
          <IconPaperclip size={18} stroke={1.75} />
        </Box>
        <Button variant="primary" onClick={onContinue} disabled={!topMatch}>
          <Flex sx={{ alignItems: 'center', gap: 1 }}>
            <Trans>Continue</Trans>
            <IconArrowRight size={16} stroke={2} />
          </Flex>
        </Button>
      </Flex>

      {query && (
        <Flex
          sx={{
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: 2,
            paddingX: 1,
          }}
        >
          <Text sx={{ fontSize: '12px', color: '#9ca3af' }}>
            <Trans>
              <Box
                as="kbd"
                sx={{
                  padding: '2px 6px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                  fontFamily: 'monospace',
                  fontSize: '11px',
                }}
              >
                Esc
              </Box>{' '}
              to clear
            </Trans>
          </Text>
        </Flex>
      )}
    </Box>
  );
}
